<template>
  <GenericUploadTable
    v-model="localImperialActive"
    :title="title"
    toggleLabel="Custom Viscosities"
    :tableData="wellProperties?.custom_viscosities_properties || []"
    :columns="displayedColumns"
    :onFileSelected="handleFileSelected"
    :onClear="removeData"
    :fileInputKey="fileInputKeyCorrelations"
    uploadLabel="Add Viscosities Sheet"
    uploadIcon="fas fa-plus-circle"
    templateDownloadUrl="/Imperial.Viscosity.Properties.template.xlsx"
    templateDownloadName="Imperial.Viscosity.Properties.template.xlsx"
    templateLinkText="Viscosity Properties Template"
  />
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
// Point this import path to wherever your GenericUploadTable.vue actually lives:
import GenericUploadTable from "@/views/scenarios/wellInfo/GenericUploadTable.vue";

import { useToast } from "primevue/usetoast";
import { read, utils } from "xlsx";
import { injectStrict } from "@/utils/utils";

import {
  correlationsFormKey,
  wellPropertiesFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { Dead_Oil_Options_Enum } from "gql/api";
import { FluidPropertyField } from "@/composables/scenarioCalculations/linearizationAlgorithm";
import { PoundsPerSquareInchAbsolute } from "@/libs/units/pressure";
import { ViscosityUnits } from "@/libs/units/viscosity";
import { Fahrenheit } from "@/libs/units/temperature";
import {
  VISCOSITY_PRESSURE_TYPE,
  VISCOSITY_TEMPERATURE_TYPE
} from "@/libs/calculations/constants";
import type { LagrangeInterpolationMapWithGroupings } from "@/composables/scenarioCalculations/lagrangeInterpolation";

const correlationsForm = injectStrict(correlationsFormKey);
const wellPropertiesForm = injectStrict(wellPropertiesFormKey);
const toaster = useToast();
const localImperialActive = ref(false);
const fileInputKeyCorrelations = ref(0);
const wellProperties = computed(() => wellPropertiesForm.data.value);
const title = "Custom Viscosities";

interface FluidPropertiesRow {
  controllingValue: number;
  controlType:
    | typeof VISCOSITY_TEMPERATURE_TYPE
    | typeof VISCOSITY_PRESSURE_TYPE;
  liveOilViscosityCP: number | null;
  waterViscosityCP: number | null;
  gasViscosityCP: number | null;
  deadOilViscosityCP: number | null;
}

const allColumns = [
  { field: FluidPropertyField.controllingValue, header: "Controlling Value" },
  { field: FluidPropertyField.controlType, header: "Control Type" },
  {
    field: FluidPropertyField.liveOilViscosityCP,
    header: "Live Oil Viscosity"
  },
  { field: FluidPropertyField.waterViscosityCP, header: "Water Viscosity" },
  { field: FluidPropertyField.gasViscosityCP, header: "Gas Viscosity" },
  { field: FluidPropertyField.deadOilViscosityCP, header: "Dead Oil Viscosity" }
];

const displayedColumns = computed(() => {
  const rows = wellProperties.value?.custom_viscosities_properties ?? [];
  if (!rows.length) return [];

  const isPressure = rows[0]?.controlType === VISCOSITY_PRESSURE_TYPE;
  const columnsWithData = allColumns.filter(col =>
    rows.some(
      (row: { [x: string]: string }) =>
        row[col.field] != null && row[col.field] !== ""
    )
  );
  return columnsWithData.map(col => {
    let headerWithUnits = col.header;
    switch (col.field) {
      case FluidPropertyField.controllingValue:
        headerWithUnits += isPressure
          ? ` (${PoundsPerSquareInchAbsolute.label})`
          : ` (${Fahrenheit.label})`;
        break;
      case FluidPropertyField.liveOilViscosityCP:
      case FluidPropertyField.waterViscosityCP:
      case FluidPropertyField.gasViscosityCP:
      case FluidPropertyField.deadOilViscosityCP:
        headerWithUnits += ` (${ViscosityUnits.Centipoise.label})`;
        break;
    }
    return { ...col, header: headerWithUnits };
  });
});

function handleFileSelected(file: File) {
  parseViscosities(file).then(rows => {
    if (rows.length === 0) {
      fileInputKeyCorrelations.value++;
      return;
    }
    const hasCustomDeadOilValues = rows?.some(
      entry => entry.deadOilViscosityCP != null
    );
    if (hasCustomDeadOilValues) {
      correlationsForm.update({
        dead_oil: Dead_Oil_Options_Enum.Custom
      });
    }
    wellPropertiesForm.update({
      custom_viscosities_properties: rows
    });
  });
}

async function parseViscosities(file: File): Promise<FluidPropertiesRow[]> {
  const buffer = await file.arrayBuffer();
  const workbook = read(buffer);
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const data: (string | number | null)[][] = utils.sheet_to_json(sheet, {
    header: 1,
    blankrows: false,
    defval: null
  });
  const dependentVariableHeader = data[5]?.[2];
  if (
    !dependentVariableHeader ||
    !dependentVariableHeader.toString().toLowerCase().includes("viscosity")
  ) {
    toaster.add({
      severity: "error",
      summary: "Fluid Properties Upload Error",
      detail: `This Sheet is for "Viscosity" only. Found: "${dependentVariableHeader ?? "N/A"}"`
    });
    return [];
  }

  const HeaderSpace = 3;
  const startRow = 7;
  const rows: FluidPropertiesRow[] = [];
  let controlType:
    | typeof VISCOSITY_TEMPERATURE_TYPE
    | typeof VISCOSITY_PRESSURE_TYPE
    | null = null;
  const controllingValueSet = new Set<number>();

  for (let i = startRow; i < data.length; i++) {
    const row = data[i];
    const isRowEmpty = row.every(cell => cell === null || cell === "");
    if (isRowEmpty) {
      continue;
    }

    const rawTemp = row[0];
    const rawPressure = row[1];
    const tempValue = typeof rawTemp === "string" ? rawTemp.trim() : rawTemp;
    const pressureValue =
      typeof rawPressure === "string" ? rawPressure.trim() : rawPressure;

    const hasDependentData = row
      .slice(2)
      .some(cell => cell != null && cell !== "");
    if (hasDependentData && !tempValue && !pressureValue) {
      toaster.add({
        severity: "error",
        summary: "Fluid Properties Upload Error",
        detail: `Row ${i + HeaderSpace}: must have either a Temperature or a Pressure.`
      });
      return [];
    }

    if (tempValue && pressureValue) {
      toaster.add({
        severity: "error",
        summary: "Fluid Properties Upload Error",
        detail: `Row ${i + HeaderSpace}: cannot have both Temperature and Pressure. Provide only one.`
      });
      return [];
    }

    if (!controlType) {
      controlType = tempValue
        ? VISCOSITY_TEMPERATURE_TYPE
        : VISCOSITY_PRESSURE_TYPE;
    } else {
      if (controlType === VISCOSITY_TEMPERATURE_TYPE && !tempValue) {
        toaster.add({
          severity: "error",
          summary: "Fluid Properties Upload Error",
          detail: `Row ${i + HeaderSpace} must have a temperature.`
        });
        return [];
      }
      if (controlType === VISCOSITY_PRESSURE_TYPE && !pressureValue) {
        toaster.add({
          severity: "error",
          summary: "Fluid Properties Upload Error",
          detail: `Row ${i + HeaderSpace} must have a pressure.`
        });
        return [];
      }
    }

    const controllingVal =
      controlType === VISCOSITY_TEMPERATURE_TYPE
        ? Number(tempValue)
        : Number(pressureValue);

    if (controllingValueSet.has(controllingVal)) {
      toaster.add({
        severity: "error",
        summary: "Fluid Properties Upload Error",
        detail: `Row ${i + HeaderSpace} has a duplicate ${controlType} value: ${controllingVal}.`
      });
      return [];
    }
    controllingValueSet.add(controllingVal);

    const liveOilViscosityCP = toNumberOrNull(row[2]);
    const waterViscosityCP = toNumberOrNull(row[3]);
    const gasViscosityCP = toNumberOrNull(row[4]);
    const deadOilViscosityCP = toNumberOrNull(row[5]);

    rows.push({
      controllingValue: controllingVal,
      controlType,
      liveOilViscosityCP,
      waterViscosityCP,
      gasViscosityCP,
      deadOilViscosityCP
    });
  }

  if (rows.length === 0) {
    toaster.add({
      severity: "error",
      summary: "Fluid Properties Upload Error",
      detail: "No data rows found in the uploaded file."
    });
    return [];
  }
  return rows;
}

function toNumberOrNull(val: unknown): number | null {
  if (val === null || val === "" || val === undefined) {
    return null;
  }
  return Number(val);
}

function removeData() {
  // Update dead oil and live oil correlations to non-custom if currently custom
  const hasCustomDeadOilValues =
    wellProperties.value?.custom_viscosities_properties?.some(
      (entry: LagrangeInterpolationMapWithGroupings) =>
        entry.deadOilViscosityCP != null
    );
  if (hasCustomDeadOilValues) {
    correlationsForm.update({
      dead_oil: Dead_Oil_Options_Enum.BeggsRobinson
    });
  }

  wellPropertiesForm.update({ custom_viscosities_properties: null });
}
</script>
